import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconStethoscope } from "@/lib/icons";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import { useLogin } from "@/lib/hooks/useAuth";
import { useAuthStore } from "@/stores/auth";

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const loginMutation = useLogin();

  const form = useForm({
    initialValues: { email: "", password: "" },
    validate: {
      email: (value) =>
        value.trim().length >= 2 ? null : t("auth.invalid_email"),
      password: (value) => (value.length >= 1 ? null : t("auth.password_required")),
    },
  });

  if (accessToken) {
    return <Navigate to="/" replace />;
  }

  const errorMessage = (() => {
    if (!loginMutation.error) return null;
    if (loginMutation.error instanceof ApiError) {
      if (loginMutation.error.code === "auth_failed") return t("auth.invalid_credentials");
      if (loginMutation.error.code === "account_locked") return t("auth.account_locked");
      return loginMutation.error.message;
    }
    return t("auth.network_error");
  })();

  const onSubmit = form.onSubmit((values) => {
    loginMutation.mutate(values, {
      onSuccess: () => {
        const from = (location.state as LocationState | undefined)?.from?.pathname ?? "/";
        navigate(from, { replace: true });
      },
    });
  });

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #eef4ff 0%, #ffffff 50%, #dce5f7 100%)",
        padding: "2rem",
      }}
    >
      <Card
        padding={0}
        radius="xl"
        shadow="xl"
        withBorder={false}
        style={{
          width: "100%",
          maxWidth: 460,
          overflow: "hidden",
          background: "white",
        }}
      >
        <Box
          p="xl"
          style={{
            background:
              "linear-gradient(135deg, var(--mantine-color-brand-6) 0%, var(--mantine-color-brand-9) 100%)",
            color: "white",
          }}
        >
          <Group gap="md">
            <ThemeIcon size={56} radius="xl" variant="white" color="brand">
              <IconStethoscope size={28} strokeWidth={1.8} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text fw={800} size="xl" lh={1.1}>
                Tibbiy Ko'rik
              </Text>
              <Text size="sm" tt="uppercase" fw={600} opacity={0.85} mt={2}>
                Stratix Med Pro
              </Text>
            </Stack>
          </Group>
        </Box>

        <Box p="xl">
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={3}>{t("auth.login_title")}</Title>
              <Text c="dimmed" size="sm">
                {t("auth.login_subtitle")}
              </Text>
            </Stack>

            {errorMessage && (
              <Alert color="red" icon={<IconAlertCircle size={16} />} radius="md">
                {errorMessage}
              </Alert>
            )}

            <form onSubmit={onSubmit}>
              <Stack>
                <TextInput
                  label={t("auth.login")}
                  placeholder={t("placeholder.login")}
                  size="md"
                  autoComplete="username"
                  {...form.getInputProps("email")}
                />
                <PasswordInput
                  label={t("auth.password")}
                  placeholder="••••••••••••"
                  size="md"
                  autoComplete="current-password"
                  {...form.getInputProps("password")}
                />
                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  loading={loginMutation.isPending}
                  variant="gradient"
                  gradient={{ from: "brand.6", to: "brand.8", deg: 135 }}
                  mt="xs"
                >
                  {t("auth.submit")}
                </Button>
              </Stack>
            </form>

            <Text c="dimmed" size="xs" ta="center" mt="md">
              Powered by Stratix Systems
            </Text>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}
